import type { OrderSource } from '../../components/customer/CustomerChips.tsx';
import axiosInstance from '../../core/axiosConfig.ts';
import * as Sentry from '@sentry/react';
import type { WoltDelivery } from '../../components/wolt/WoltTimeline.tsx';
import type { DineInInfo, OrderType } from '../../components/order/DineIn.tsx';

export type PastOrderOutcome = 'DELIVERED' | 'REJECTED' | 'CANCELLED';

export type PastOrderRow = {
    id: number;
    orderNumber: string;
    /** The restaurant's number for that day, "VLO-007"; null for orders before daily numbers. */
    dailyNumber?: string | null;
    /** UTC with a Z. */
    placedAt: string | null;
    closedAt: string | null;
    restaurantId: number;
    restaurantName: string;
    customerName: string | null;
    paymentType: 'CASH' | 'ONLINE';
    outcome: PastOrderOutcome;
    currency: string | null;
    itemSubTotal: number;
    deliveryFee: number | null;
    serviceFee: number | null;
    creditRedeemed: number | null;
    totalPrice: number | null;
    /** WOLT, VELLO_COURIER, or null when nothing was delivered. */
    deliveredBy: 'WOLT' | 'VELLO_COURIER' | null;
    courierName: string | null;
    /** Where it was placed; null/absent for orders before 21 Oct 2026 or an older backend. */
    src?: OrderSource | null;
    /** The customer's earlier orders at this restaurant that went through; absent on an older backend. */
    prev?: number | null;
    /** DELIVERY, DINE_IN or PICKUP; absent on an older backend (= delivery). */
    orderType?: OrderType | null;
    dineIn?: DineInInfo | null;
};

export type PastOrderLine = {
    name: string | null;
    quantity: number;
    /** Already includes the options. */
    unitPrice: number | null;
    lineTotal: number | null;
    options: { category: string | null; name: string | null; price: number | null }[];
};

export type PastOrderTimelineEntry = {
    at: string | null;
    kind: 'STATUS' | 'COURIER' | 'WOLT';
    label: string;
    detail: string | null;
};

export type PastOrderDetails = {
    order: PastOrderRow;
    notes: string | null;
    /** Admins only. */
    courierPhone: string | null;
    lines: PastOrderLine[];
    timeline: PastOrderTimelineEntry[];
    woltDelivery: WoltDelivery | null;
    /** What Wolt charges for the delivery. Admins only. */
    woltPrice: { amount: number; currency: string | null } | null;
    /** Vello's commission on this order. Admins only; null/absent for a restaurant login or an older backend. */
    commission?: PastOrderCommission | null;
};

/**
 * The commission snapshot on the order: DELIVERY = the venue's delivery rate,
 * ON_SITE = its pickup & dine-in rate. ratePercent is null for old orders
 * without a snapshot rate (the amount is still sent).
 */
export type PastOrderCommission = {
    ratePercent: number | null;
    amount: number | null;
    basis: 'DELIVERY' | 'ON_SITE' | null;
    /** True once the order was rejected, cancelled or refunded: Vello keeps none of it. Absent on an older backend. */
    cancelled?: boolean | null;
};

export type PastOrderFilters = {
    from?: string | null;
    to?: string | null;
    /** Ignored by the server for restaurant logins, which only ever see their own. */
    restaurantIds?: number[];
    outcome?: PastOrderOutcome | null;
};

const totalFrom = (response: { headers: Record<string, unknown> }, fallback: number) => {
    const parsed = Number(response.headers['x-total-count'] ?? response.headers['X-Total-Count']);
    return Number.isFinite(parsed) ? parsed : fallback;
};

export default class PastOrderService {
    async list(filters: PastOrderFilters, start: number, end: number): Promise<{ rows: PastOrderRow[]; total: number }> {
        try {
            const params = new URLSearchParams();
            if (filters.from) params.append('from', filters.from);
            if (filters.to) params.append('to', filters.to);
            (filters.restaurantIds ?? []).forEach((id) => params.append('restaurantIds', String(id)));
            if (filters.outcome) params.append('outcome', filters.outcome);
            params.append('_start', String(start));
            params.append('_end', String(end));
            const response = await axiosInstance.get(`/admin/orders/past?${params.toString()}`);
            return { rows: response.data, total: totalFrom(response, response.data.length) };
        } catch (e) {
            Sentry.captureException(e, { tags: { source: 'past-orders' } });
            throw e;
        }
    }

    async details(orderId: number): Promise<PastOrderDetails> {
        try {
            const response = await axiosInstance.get(`/admin/orders/past/${orderId}`);
            return response.data;
        } catch (e) {
            Sentry.captureException(e, { tags: { source: 'past-order-details' } });
            throw e;
        }
    }
}
